// classes/SupportClass.js
export const SupportClass = {
  id: 'support',
  name: 'Support',
  description: 'Medical and tactical support specialist, keeps the squad alive',
  icon: '🏥',
  recruitCost: 0,
  
  baseStats: {
    health: 90,
    maxHealth: 90,
    aim: 70,
    mobility: 70,
    defense: 55,
    willpower: 90,
    critChance: 10
  },

  skills: [
    // TIER 1
    {
      id: 'field_medic',
      name: 'Field Medic',
      tier: 1,
      xpRequired: 0,
      description: 'Basic medical training. Medkits heal +25% more',
      effect: { healBonus: 25 }
    },
    {
      id: 'smoke_grenade',
      name: 'Smoke Grenade',
      tier: 1,
      xpRequired: 25,
      description: 'Deploy smoke that grants +40 defense to allies inside',
      effect: { smokeDefenseBonus: 40 }
    },

    // TIER 2
    {
      id: 'stabilize',
      name: 'Stabilize',
      tier: 2,
      xpRequired: 60,
      description: 'Wounded allies recover 10 health per turn when near you',
      effect: { allyRegenRadius: 5, regenAmount: 10 }
    },
    {
      id: 'covering_fire',
      name: 'Covering Fire',
      tier: 2,
      xpRequired: 60,
      description: 'Grants +15 defense to adjacent allies',
      effect: { allyDefenseBonus: 15 }
    },

    // TIER 3
    {
      id: 'combat_medic',
      name: 'Combat Medic',
      tier: 3,
      xpRequired: 125,
      description: 'Can revive critically wounded soldiers in combat',
      effect: { canRevive: true, reviveHealthPercent: 50 }
    },
    {
      id: 'suppression_immunity',
      name: 'Suppression Immunity',
      tier: 3,
      xpRequired: 125,
      description: 'Cannot be suppressed or disoriented by enemy attacks',
      effect: { suppressionImmune: true }
    },

    // TIER 4
    {
      id: 'field_surgeon',
      name: 'Field Surgeon',
      tier: 4,
      xpRequired: 200,
      description: 'Medkits heal +50% more and remove negative status effects',
      effect: { healBonus: 50, removeDebuffs: true }
    },
    {
      id: 'inspiration',
      name: 'Inspiration',
      tier: 4,
      xpRequired: 200,
      description: 'Nearby allies gain +10 aim and +20 willpower',
      effect: { inspirationRadius: 6, aimBonus: 10, willpowerBonus: 20 }
    },

    // TIER 5
    {
      id: 'rapid_deployment',
      name: 'Rapid Deployment',
      tier: 5,
      xpRequired: 300,
      description: 'Can use medkit and still take other actions in same turn',
      effect: { medkitNoActionCost: true }
    },
    {
      id: 'tactical_genius',
      name: 'Tactical Genius',
      tier: 5,
      xpRequired: 300,
      description: 'Squad gains +1 action point on first turn of combat',
      effect: { squadFirstTurnAP: 1 }
    },

    // TIER 6
    {
      id: 'savior',
      name: 'Savior',
      tier: 6,
      xpRequired: 450,
      description: 'Healing also grants 25% of healed amount as temporary bonus health',
      effect: { healGrantsTempHP: 25 }
    },
    {
      id: 'guardian_angel',
      name: 'Guardian Angel',
      tier: 6,
      xpRequired: 450,
      description: 'Once per mission, automatically revive a fallen ally at 50% health',
      effect: { autoRevive: true, autoReviveHealthPercent: 50 }
    },

    // TIER 7
    {
      id: 'miracle_worker',
      name: 'Miracle Worker',
      tier: 7,
      xpRequired: 625,
      description: 'Medkits restore full health and grant immunity to damage for 1 turn',
      effect: { medkitFullHeal: true, healGrantsInvuln: true }
    },
    {
      id: 'combat_presence',
      name: 'Combat Presence',
      tier: 7,
      xpRequired: 625,
      description: 'All allies on the battlefield gain +5 aim and +10 defense',
      effect: { globalAllyBonus: { aim: 5, defense: 10 } }
    },

    // TIER 8
    {
      id: 'valkyrie',
      name: 'Valkyrie',
      tier: 8,
      xpRequired: 875,
      description: 'Revived allies return at 100% health and gain +30 aim for 2 turns',
      effect: { reviveFullHealth: true, reviveAimBonus: 30, reviveBonusTurns: 2 }
    },
    {
      id: 'fortress_protocol',
      name: 'Fortress Protocol',
      tier: 8,
      xpRequired: 875,
      description: 'Create a defensive zone: allies inside take 50% less damage',
      effect: { fortressZoneRadius: 8, damageReduction: 50 }
    },

    // TIER 9
    {
      id: 'field_commander',
      name: 'Field Commander',
      tier: 9,
      xpRequired: 1250,
      description: 'The ultimate support. All allies gain +20 to all stats and 1 extra action point',
      effect: { 
        globalAllyBonus: { 
          health: 20, aim: 20, mobility: 20, defense: 20, willpower: 20 
        },
        squadBonusActionPoints: 1
      }
    }
  ],

  abilities: [
    {
      id: 'medkit',
      name: 'Medkit',
      unlockAtXP: 0,
      cooldown: 0,
      description: 'Heal an ally for 40 HP',
      usesPerMission: 3
    },
    {
      id: 'smoke_bomb',
      name: 'Smoke Bomb',
      unlockAtXP: 75,
      cooldown: 2,
      description: 'Create smoke cover that grants defense bonus to all inside',
      usesPerMission: null
    },
    {
      id: 'revival_protocol',
      name: 'Revival Protocol',
      unlockAtXP: 250,
      cooldown: 5,
      description: 'Revive a critically wounded ally at 60% health',
      usesPerMission: 2
    },
    {
      id: 'guardian',
      name: 'Guardian',
      unlockAtXP: 500,
      cooldown: 4,
      description: 'Protect an ally, redirecting 75% of damage to yourself for 2 turns',
      usesPerMission: 2
    },
    {
      id: 'final_stand',
      name: 'Final Stand',
      unlockAtXP: 1000,
      cooldown: 6,
      description: 'All allies become immune to death for 2 turns (minimum 1 HP)',
      usesPerMission: 1
    }
  ]
};